import type { Component } from 'solid-js'
import { For, Show } from 'solid-js'
import { Trans, useTransContext } from '@mbarzda/solid-i18next'
import type { SectionsComparison } from '../../../features/engagement/domain/models/sections'
import SectionsComparisonChart from './SectionsComparisonChart'
import { getPaidVsFreeHeadline, getSegmentCards } from './SectionsPerUserBlock.vm'

interface SectionsPerUserBlockProps {
  comparison: SectionsComparison
}

// Per-user tier comparison: one card per segment (API order), an optional paid-vs-free
// headline, and the per-section grouped bars. All values are per identified web user.
const SectionsPerUserBlock: Component<SectionsPerUserBlockProps> = (props) => {
  const [t] = useTransContext()

  const cards = () => getSegmentCards(props.comparison)
  const headline = () => getPaidVsFreeHeadline(props.comparison)

  return (
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-4 sm:flex-row">
        <For each={cards()}>
          {(card) => (
            <div
              class="flex-1 rounded-lg border bg-white p-6 shadow-sm"
              classList={{ 'border-2': card.highlighted, 'border-gray-200': !card.highlighted }}
              style={card.highlighted ? { 'border-color': card.accentColor } : undefined}
            >
              <div class="flex items-center gap-2">
                <span class="h-2.5 w-2.5 rounded-full" style={{ 'background-color': card.accentColor }} />
                <span class="text-sm font-semibold text-gray-900">{card.label}</span>
              </div>
              <p class="mt-3 text-xl font-bold text-gray-900">{card.time}</p>
              <p class="text-xs font-semibold text-gray-500">
                <Trans key="ls_sections_per_user_time" />
              </p>
              <div class="mt-3 flex gap-6">
                <div>
                  <p class="text-sm font-bold text-gray-900">{card.visitsPerUser}</p>
                  <p class="text-xs text-gray-500">
                    <Trans key="ls_sections_per_user_visits" />
                  </p>
                </div>
                <div>
                  <p class="text-sm font-bold text-gray-900">{card.interactionsPerUser}</p>
                  <p class="text-xs text-gray-500">
                    <Trans key="ls_sections_per_user_interactions" />
                  </p>
                </div>
              </div>
            </div>
          )}
        </For>
      </div>

      <Show when={headline()}>
        {(h) => (
          <div class="flex items-center gap-3 rounded-lg border border-blue-200 border-l-4 border-l-blue-500 bg-blue-50 px-4 py-3">
            <span class="text-lg font-bold leading-none text-blue-600">{h().multiplier}&times;</span>
            <p class="text-base font-semibold text-gray-900">
              {t('ls_sections_paid_vs_free_headline_short', { tier: h().tier })}
            </p>
          </div>
        )}
      </Show>

      <SectionsComparisonChart comparison={props.comparison} />
    </div>
  )
}

export default SectionsPerUserBlock
